import { LOCAL_STORAGE_KEYS, WORKSPACE_ENVIRONMENT } from '@/app.constants';

export interface CustomHeader {
  key: string;
  value: string;
}

export interface CustomEnvOverride {
  key: string;
  value: string;
}

// Check if we're in development environment
const isDevelopment = import.meta.env.VITE_ENVIRONMENT === WORKSPACE_ENVIRONMENT.DEVELOPMENT;

export const getCustomHeaders = (): CustomHeader[] => {
  if (!isDevelopment) {
    return [];
  }
  const customHeaders = localStorage.getItem(LOCAL_STORAGE_KEYS.DEVELOPER_CUSTOM_HEADERS);
  try {
    return customHeaders ? JSON.parse(customHeaders) : [];
  } catch (error) {
    console.error('Failed to parse custom headers:', error);
    return [];
  }
};

export const getCustomHeadersObject = (): Record<string, string> => {
  if (!isDevelopment) {
    return {};
  }
  const customHeaders = getCustomHeaders();
  return Object.fromEntries(
    customHeaders.map(header => [header.key, header.value])
  );
};

export const setCustomHeaders = (headers: CustomHeader[]) => {
  if (!isDevelopment) {
    return;
  }
  if (!headers || headers.length === 0) {
    removeCustomHeaders();
    return;
  }
  localStorage.setItem(LOCAL_STORAGE_KEYS.DEVELOPER_CUSTOM_HEADERS, JSON.stringify(headers));
};

export const removeCustomHeaders = () => {
  if (!isDevelopment) {
    return;
  }
  localStorage.removeItem(LOCAL_STORAGE_KEYS.DEVELOPER_CUSTOM_HEADERS);
};

export const getCustomBaseUrl = (): string => {
  if (!isDevelopment) {
    return '';
  }
  const customBaseUrl = localStorage.getItem(LOCAL_STORAGE_KEYS.DEVELOPER_CUSTOM_BASE_URL) || '';
  return customBaseUrl.replace(/\/$/, '');
};

export const setCustomBaseUrl = (url: string) => {
  if (!isDevelopment) {
    return;
  }
  if (!url || !url.trim()) {
    removeCustomBaseUrl();
    return;
  }
  localStorage.setItem(LOCAL_STORAGE_KEYS.DEVELOPER_CUSTOM_BASE_URL, url.trim().replace(/\/$/, ''));
};

export const removeCustomBaseUrl = () => {
  if (!isDevelopment) {
    return;
  }
  localStorage.removeItem(LOCAL_STORAGE_KEYS.DEVELOPER_CUSTOM_BASE_URL);
};

export const getCustomEnvOverrides = (): CustomEnvOverride[] => {
  if (!isDevelopment) {
    return [];
  }
  const customEnvOverrides = localStorage.getItem(LOCAL_STORAGE_KEYS.DEVELOPER_ENV_OVERRIDES);
  try {
    return customEnvOverrides ? JSON.parse(customEnvOverrides) : [];
  } catch (error) {
    console.error('Failed to parse custom env overrides:', error);
    return [];
  }
};

export const setCustomEnvOverrides = (envOverrides: CustomEnvOverride[], allowedKeys: string[]) => {
  if (!isDevelopment) {
    return;
  }
  const allowedSet = new Set<string>(allowedKeys);
  const sanitizedOverrides = (envOverrides || []).filter(
    (override) => allowedSet.has(override?.key)
  );
  if (!sanitizedOverrides.length) {
    removeCustomEnvOverrides();
    return;
  }
  localStorage.setItem(LOCAL_STORAGE_KEYS.DEVELOPER_ENV_OVERRIDES, JSON.stringify(sanitizedOverrides));
};

export const removeCustomEnvOverrides = () => {
  if (!isDevelopment) {
    return;
  }
  localStorage.removeItem(LOCAL_STORAGE_KEYS.DEVELOPER_ENV_OVERRIDES);
};
